import { useCallback, useState } from "react"
import { canReviewPosition, lastPhaseOf } from "@/store/selectors"
import type { Position } from "@/store/selectors"
import type { Phase, Round, Session } from "@/types"

/*
 * Which round THIS browser is looking at, as opposed to where the room is.
 *
 * The only piece of session state in the app that is deliberately local, and the
 * exception proves the rule it is written against: everything the ROOM is meant
 * to follow lives on a document, because a facilitator's action has to move every
 * screen. Looking back at a finished round is the opposite act — one person
 * re-reading what has already been decided — and putting it on the session
 * document would drag the whole workshop backwards every time somebody checked
 * what they had voted for.
 *
 * That is also what makes it the same feature on both surfaces. It writes
 * nothing, so there is no rule to satisfy and no permission for a facilitator
 * and a guest to differ on.
 *
 * The cost of it being local is that a review is READ ONLY, and not as a
 * courtesy: a screen showing the Pains round while the session document says
 * `cards` would otherwise be writing votes into a round the room has left, under
 * a `donePhase` key nothing is counting any more. `reviewing` is threaded down to
 * every round screen for exactly that reason.
 *
 * Held by `Session` and passed down, rather than put in a context: three
 * components read it and all three are its children. It is also NOT in the
 * store — the store mirrors Firestore documents, and this is the one answer no
 * document has.
 */

export interface SessionView {
  /** The round on screen. The room's own unless this browser has looked back. */
  round: Round
  phase: Phase
  /** True while the position on screen is behind the room's. Nothing may be written. */
  reviewing: boolean
  /**
   * Open a finished position. A round with no phase given opens where the room
   * left it — see `lastPhaseOf`. Anything not strictly behind the room is
   * refused rather than clamped, so a stale click cannot move the screen
   * forward.
   */
  review: (round: Round, phase?: Phase) => void
  /** Back to wherever the room is. */
  live: () => void
}

export function useSessionView(session: Session | null): SessionView {
  const [looking, setLooking] = useState<Position | null>(null)

  /*
   * The room moving is what ends a review.
   *
   * A workshop is run out loud, and the moment the facilitator takes everyone
   * somewhere else is the moment you are meant to be looking at it — a screen
   * left behind on a round nobody is discussing any more is how someone misses
   * their turn. The overlay counts as a move: the curtain is how the room is
   * TOLD, and it goes up without changing round or phase.
   *
   * Adjusted during the render that brings the new position in, rather than in
   * an effect: the alternative renders the stale round once and corrects it on a
   * second pass, which for one frame shows a review that has already ended.
   */
  const here = session ? `${session.round}:${session.phase}:${session.overlay?.kind ?? ""}` : ""
  const [wasAt, setWasAt] = useState(here)
  if (wasAt !== here) {
    setWasAt(here)
    setLooking(null)
  }

  const review = useCallback(
    (target: Round, targetPhase?: Phase) => {
      const wanted = targetPhase ?? lastPhaseOf(target)
      if (!canReviewPosition(session, target, wanted)) return
      setLooking({ round: target, phase: wanted })
    },
    [session]
  )

  const live = useCallback(() => setLooking(null), [])

  /*
   * `looking` is dropped on the render that notices the room has moved, but this
   * object is built from the same render's state — so it is read through
   * `wasAt === here` rather than on its own. Without that the one render between
   * the move landing and the reset would still report the old position.
   */
  const behind = wasAt === here ? looking : null

  return {
    round: behind?.round ?? session?.round ?? "lobby",
    phase: behind?.phase ?? session?.phase ?? "setup",
    reviewing: behind !== null,
    review,
    live,
  }
}
